import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import styles from './Search.module.css';
import SEO from '../components/SEO';

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);

  // All searchable content
  const searchableContent = [
    {
      title: 'Skilled Nursing',
      description: 'Registered nurses provide skilled care and instruction to patients and families, including wound care, medication supervision, catheter care, treatments and injections, and I.V. therapy.',
      path: '/services/skilled-nursing',
      category: 'Services'
    },
    {
      title: 'Physical Therapy',
      description: 'Licensed physical therapists help patients regain strength, mobility, and independence through personalized treatment plans.',
      path: '/services/physical-therapy',
      category: 'Services'
    },
    {
      title: 'Occupational Therapy',
      description: 'Occupational therapists assist patients in developing, recovering, and maintaining daily living skills for improved quality of life.',
      path: '/services/occupational-therapy',
      category: 'Services'
    },
    {
      title: 'Speech Therapy',
      description: 'Speech-language pathologists help patients improve communication, swallowing difficulties, and cognitive skills.',
      path: '/services/speech-therapy',
      category: 'Services'
    },
    {
      title: 'Social Services',
      description: 'Social workers provide counseling, community resource connections, and support for patients and families navigating healthcare challenges.',
      path: '/services/social-services',
      category: 'Services'
    },
    {
      title: 'Certified Home Health Aid',
      description: 'Certified aides assist patients with personal care, meal preparation, ambulation, and exercises under professional supervision.',
      path: '/services/certified-home-health-aid',
      category: 'Services'
    },
    {
      title: 'Alzheimer\'s Care',
      description: 'Specialized care and support for patients with Alzheimer\'s disease and dementia, focusing on safety, dignity, and quality of life.',
      path: '/services/alzheimers-care',
      category: 'Services'
    },
    {
      title: 'Geriatric Care Management',
      description: 'Comprehensive care coordination and advocacy services to help seniors and their families navigate complex healthcare systems.',
      path: '/services/geriatric-care-management',
      category: 'Services'
    },
    {
      title: 'Medication Reminders',
      description: 'Professional medication management services to ensure proper medication adherence and prevent missed doses.',
      path: '/services/medication-reminders',
      category: 'Services'
    },
    {
      title: 'Post-hospitalization Care',
      description: 'Transitional care services to support safe recovery at home after hospital discharge.',
      path: '/services/post-hospitalization-care',
      category: 'Services'
    },
    {
      title: 'About Us',
      description: 'Learn about our mission, values, and commitment to providing exceptional home health care services.',
      path: '/about',
      category: 'Company'
    },
    {
      title: 'Contact',
      description: 'Get in touch with our team to learn more about our services or schedule a consultation.',
      path: '/contact',
      category: 'Company'
    },
    {
      title: 'Home',
      description: 'Home Health Care Right Where You Belong - Professional care in the comfort of your home.',
      path: '/',
      category: 'Pages'
    },
    {
      title: 'Learn More',
      description: 'Discover how All For You Home Health Care can help you or your loved ones maintain independence and quality of life.',
      path: '/learn-more',
      category: 'Pages'
    }
  ];

  useEffect(() => {
    const query = searchParams.get('q');
    if (query) {
      setSearchQuery(query);
      performSearch(query);
    }
  }, [searchParams]);

  // Calculate Levenshtein distance for fuzzy matching
  const levenshteinDistance = (str1, str2) => {
    const track = Array(str2.length + 1).fill(null).map(() =>
      Array(str1.length + 1).fill(null));
    for (let i = 0; i <= str1.length; i += 1) {
      track[0][i] = i;
    }
    for (let j = 0; j <= str2.length; j += 1) {
      track[j][0] = j;
    }
    for (let j = 1; j <= str2.length; j += 1) {
      for (let i = 1; i <= str1.length; i += 1) {
        const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
        track[j][i] = Math.min(
          track[j][i - 1] + 1,
          track[j - 1][i] + 1,
          track[j - 1][i - 1] + indicator
        );
      }
    }
    return track[str2.length][str1.length];
  };

  // Check if query fuzzy matches the text
  const fuzzyMatch = (text, query) => {
    text = text.toLowerCase();
    query = query.toLowerCase();

    // Exact match or contains
    if (text.includes(query)) {
      return { match: true, score: 100 };
    }

    // Check each word in the text
    const words = text.split(/\s+/);
    const queryWords = query.split(/\s+/);

    let bestScore = 0;

    for (const queryWord of queryWords) {
      for (const word of words) {
        const distance = levenshteinDistance(queryWord, word);
        const maxLen = Math.max(queryWord.length, word.length);
        const similarity = ((maxLen - distance) / maxLen) * 100;

        // If similarity is high enough (allow 1-2 character difference)
        if (similarity > 70) {
          bestScore = Math.max(bestScore, similarity);
        }
      }
    }

    return { match: bestScore > 70, score: bestScore };
  };

  const performSearch = (query) => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    // Search with fuzzy matching and scoring
    const scoredResults = searchableContent.map(item => {
      const titleMatch = fuzzyMatch(item.title, query);
      const descMatch = fuzzyMatch(item.description, query);
      const catMatch = fuzzyMatch(item.category, query);

      const maxScore = Math.max(titleMatch.score, descMatch.score, catMatch.score);
      const isMatch = titleMatch.match || descMatch.match || catMatch.match;

      return {
        ...item,
        score: maxScore,
        isMatch
      };
    });

    // Filter matches and sort by score
    const filtered = scoredResults
      .filter(item => item.isMatch)
      .sort((a, b) => b.score - a.score);

    setResults(filtered);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery.trim() });
    }
  };

  const highlightText = (text, query) => {
    if (!query.trim()) return text;

    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? <mark key={index} className={styles.highlight}>{part}</mark> : part
    );
  };

  return (
    <div className={styles.searchPage}>
      <SEO
        title={searchQuery ? `Search Results for "${searchQuery}"` : "Search"}
        description="Search All For You Home Health Care website for services, information, and resources about home health care in Southern California."
        keywords="search, find services, home health care search"
        canonicalUrl="https://allforyouhc.com/search"
      />
      <div className={styles.searchHeader}>
        <div className={styles.container}>
          <form onSubmit={handleSearch} className={styles.searchForm}>
            <svg className={styles.searchIcon} width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="2"/>
              <path d="M15 15l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search for services, information..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </form>
        </div>
      </div>

      <div className={styles.resultsSection}>
        <div className={styles.container}>
          {searchQuery && (
            <p className={styles.resultsCount}>
              {results.length} {results.length === 1 ? 'result' : 'results'} for "{searchQuery}"
            </p>
          )}

          {results.length > 0 ? (
            <div className={styles.resultsGrid}>
              {results.map((result, index) => (
                <Link to={result.path} key={index} className={styles.resultCard}>
                  <span className={styles.resultCategory}>{result.category}</span>
                  <h3 className={styles.resultTitle}>
                    {highlightText(result.title, searchQuery)}
                  </h3>
                  <p className={styles.resultDescription}>
                    {highlightText(result.description, searchQuery)}
                  </p>
                  <span className={styles.resultLink}>
                    Learn more →
                  </span>
                </Link>
              ))}
            </div>
          ) : searchQuery ? (
            <div className={styles.noResults}>
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="26" cy="26" r="18" stroke="#d1d5db" strokeWidth="4"/>
                <path d="M38 38l16 16" stroke="#d1d5db" strokeWidth="4" strokeLinecap="round"/>
              </svg>
              <h3>No results found</h3>
              <p>Try searching for something else</p>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <h3>Start searching</h3>
              <p>Enter a search term to find services and information</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Search;
